/**
 * Payment Configuration Module
 * 
 * ⚠️ IMPORTANT: This file manages domain-to-organization mapping only.
 * Payment credentials are NO LONGER stored in environment variables.
 * 
 * Credential Management (As of Unified Checkout Implementation):
 * - All payment credentials (API keys, auth tokens) are stored in the database (club_payment_configs table)
 * - Stax FM_KEY: Fetched dynamically from backend endpoint /api/camp/get_public_payment_config
 * - Forte api_login_id: Fetched dynamically from backend endpoint /api/camp/get_public_payment_config
 * - Forte credentials: Loaded server-side via Payment_config_model, never exposed to frontend
 * - This prevents credential leakage and simplifies credential rotation
 * 
 * The PAYMENT_CONFIGS object below is a lightweight structure used only for
 * determining which processor to use for each organization/merchant combination.
 */

// Domain to organization mapping
const DOMAIN_ORG_MAP = {
  "tuxedo.beclutch.club": "tuxedo",
  "warwick.beclutch.club": "warwick",
  "yaccamp.beclutch.club": "yac",
  "demo.beclutch.club": "demo",
  // Development domains
  "devtuxedo.beclutch.club": "tuxedo",
  "devtuxedocamp.beclutch.club": "tuxedo",
  "devwarwick.beclutch.club": "warwick",
  "devyaccamp.beclutch.club": "yac"
};

/**
 * Organization-to-merchant mapping (no credentials stored here)
 * Used only to determine which payment processor to initialize
 */
const PAYMENT_CONFIGS = {
  stax: {
    yac: true,
    demo: true,
    tuxedo: false,  // Tuxedo uses Forte
    warwick: false  // Warwick uses Forte
  },
  forte: {
    demo: true,
    tuxedo: true,
    warwick: true,
    yac: false  // YAC uses Stax
  }
};

/**
 * Get the organization name from a domain
 * @param {string} domain - The domain to check
 * @returns {string|null} The organization name or null if not found
 */
const getOrganizationFromDomain = (domain) => {
  if (DOMAIN_ORG_MAP[domain]) return DOMAIN_ORG_MAP[domain];
  // Allow any beclutch.club subdomain — payment config is fetched from the DB at checkout
  if (domain && domain.endsWith('.beclutch.club')) return domain.split('.')[0];
  return null;
};

/**
 * Check if a merchant is configured for an organization
 * 
 * NOTE: This does NOT return credentials. Credentials are fetched dynamically
 * from the backend API (/api/camp/get_public_payment_config).
 * This function only validates that a payment processor should be initialized for
 * the given domain and merchant combination.
 * 
 * @param {string} domain - The current domain
 * @param {string} merchant - The merchant type ("stax" or "forte")
 * @returns {Object|null} - Lightweight config object with merchant and org, or null if not configured
 */
const getPaymentConfig = (domain, merchant) => {
  const org = getOrganizationFromDomain(domain);
  if (!org) {
    console.error(`No organization found for domain: ${domain}`);
    return null;
  }

  if (merchant !== 'stax' && merchant !== 'forte') {
    console.error(`Invalid merchant type provided: ${merchant}`);
    return null;
  }

  const isConfigured = PAYMENT_CONFIGS[merchant]?.[org];

  if (!isConfigured) {
    console.error(`${merchant} is not configured for organization: ${org} (domain: ${domain})`);
    return null;
  }
  
  // Return lightweight config (credentials fetched separately from API)
  return { merchant, org };
};

/**
 * Check if the current configuration is for Stax
 * @param {string} merchant - The merchant type
 * @returns {boolean} True if the configuration is for Stax
 */
const isStaxConfig = (merchant) => {
  return merchant === "stax";
};

export {
  getPaymentConfig,
  getOrganizationFromDomain,
  isStaxConfig
}; 